// 한 경기 카드 — 코트/타입/배지 헤더, 양쪽 사이드 이름·승패·점수.
//
// 스왑 가능 여부와 카드 클릭 동작은 호출 측(legacy.ts)에서 미리 계산해
// 프롭으로 내려준다. 이 컴포넌트는 ev나 state를 직접 보지 않고, 받은
// 데이터/콜백만으로 렌더만 담당.

interface MatchCardProps {
  m: any;
  surfaceMap?: Map<string, string> | null;
  readOnly: boolean;
  swapEnabled: boolean;
  isSelfSwap: boolean;
  myPid: string | null;
  onCardClick: (() => void) | null;
  onPlayerSwap: (pid: string) => void;
  // personal "내 모임"은 명단 전원이 외부인이라 "게" 칩이 모두에게 붙어 의미 없음.
  // 클럽 정기/특별전에선 회원/게스트 구분이 운영상 의미 있어 기본 false.
  hideGuestSuffix?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  men_doubles: "남복",
  women_doubles: "여복",
  mixed_doubles: "혼복",
  exceptional_mix: "잡복",
  open_doubles: "복식",
};

function wlClass(side: "side_a" | "side_b", winner: string | null): string {
  if (winner === side) return "match__wl match__wl--win";
  if (winner === "draw") return "match__wl match__wl--draw";
  if (winner) return "match__wl match__wl--lose";
  return "";
}

function wlText(side: "side_a" | "side_b", winner: string | null): string {
  if (winner === side) return "승";
  if (winner === "draw") return "무";
  if (winner) return "패";
  return "";
}

function PlayerNames({
  players,
  canSwap,
  onPlayerSwap,
  hideGuestSuffix,
}: {
  players: any[];
  canSwap: (pid: string) => boolean;
  onPlayerSwap: (pid: string) => void;
  hideGuestSuffix?: boolean;
}) {
  const items = players || [];
  if (items.length === 0) return <>-</>;
  const nodes: any[] = [];
  items.forEach((p, i) => {
    if (i > 0) nodes.push(" · ");
    const inner = p.participant || p;
    const rawName = inner.display_name || p.display_name || "-";
    const isGuest = (inner.participant_type || p.participant_type) === "guest";
    const pid = p.event_participant_id;
    if (pid && canSwap(pid)) {
      nodes.push(
        <span
          class="match__player"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onPlayerSwap(pid);
          }}
        >
          {rawName}
        </span>,
      );
    } else {
      nodes.push(rawName);
    }
    if (isGuest && !hideGuestSuffix && rawName !== "-") {
      nodes.push(<span class="guest-chip">게</span>);
    }
  });
  return <>{nodes}</>;
}

export function MatchCard({
  m,
  surfaceMap,
  readOnly,
  swapEnabled,
  isSelfSwap,
  myPid,
  onCardClick,
  onPlayerSwap,
  hideGuestSuffix,
}: MatchCardProps) {
  const sides = m.sides || [];
  const sideA = sides.find((s: any) => s.side_number === 1) || { players: [] };
  const sideB = sides.find((s: any) => s.side_number === 2) || { players: [] };
  const isSkipped = m.result_status === "skipped";
  const hasResult = m.result_status && m.result_status !== "empty" && !isSkipped;
  const winner: string | null = hasResult ? m.winner_side : null;
  const canSwap = (pid: string) => swapEnabled && (!isSelfSwap || pid === myPid);

  const courtLabel = (() => {
    const base = m.court_label ? `코트 ${m.court_label}` : "코트 미배정";
    const sf = surfaceMap && m.event_court_id && surfaceMap.get(m.event_court_id);
    return sf ? `${base} · ${sf}` : base;
  })();
  const typeLabel = TYPE_LABELS[m.match_type] || "";

  const cardClass =
    (readOnly ? "match" : "match match--tap") + (isSkipped ? " match--skipped" : "");
  const scoreAClass =
    "match__score" +
    (!hasResult ? " match__score--empty" : winner === "side_a" ? " match__score--winner" : "");
  const scoreBClass =
    "match__score" +
    (!hasResult ? " match__score--empty" : winner === "side_b" ? " match__score--winner" : "");

  return (
    <div class={cardClass} onClick={onCardClick ?? undefined}>
      <div class="match__head">
        <span class="match__court">{courtLabel}</span>
        {typeLabel ? (
          <span
            class={
              "match__type-badge" +
              (m.match_type === "exceptional_mix" ? " match__type-badge--exc" : "")
            }
          >
            {typeLabel}
          </span>
        ) : (
          <span />
        )}
        <div class="match__head-right">
          {m.adhoc && !hasResult ? (
            <span class="match__edited-badge">추가됨</span>
          ) : !m.adhoc && m.manually_edited && !hasResult ? (
            <span class="match__edited-badge">수정됨</span>
          ) : null}
          {isSkipped ? <span class="badge badge--skipped">미진행</span> : null}
          {hasResult ? (
            <span class="badge badge--result">{winner === "draw" ? "무승부" : "결과"}</span>
          ) : null}
        </div>
      </div>
      {m.match_note ? <div class="match__note">{m.match_note}</div> : null}
      <div class="match__body">
        <div
          class={
            "match__side" +
            (hasResult && winner === "side_a" ? " match__side--winner" : "")
          }
        >
          <div class="match__names">
            <PlayerNames players={sideA.players} canSwap={canSwap} onPlayerSwap={onPlayerSwap} hideGuestSuffix={hideGuestSuffix} />
            {hasResult ? (
              <span class={wlClass("side_a", winner)}>{wlText("side_a", winner)}</span>
            ) : null}
          </div>
          <div class={scoreAClass} style="margin-top:6px">
            {hasResult ? String(m.side_a_score ?? "-") : "-"}
          </div>
        </div>
        <div class="match__vs">VS</div>
        <div
          class={
            "match__side match__side--right" +
            (hasResult && winner === "side_b" ? " match__side--winner" : "")
          }
        >
          <div class="match__names">
            <PlayerNames players={sideB.players} canSwap={canSwap} onPlayerSwap={onPlayerSwap} hideGuestSuffix={hideGuestSuffix} />
            {hasResult ? (
              <span class={wlClass("side_b", winner)}>{wlText("side_b", winner)}</span>
            ) : null}
          </div>
          <div class={scoreBClass} style="margin-top:6px;margin-left:auto">
            {hasResult ? String(m.side_b_score ?? "-") : "-"}
          </div>
        </div>
      </div>
    </div>
  );
}
